import { Checkbox } from '@mui/material';
import { Dispatch, SetStateAction, useCallback } from 'react';
import { DataDeletionType, TextRange } from 'src/graphql';
import { FormState } from 'src/types';
import { Badge } from '../../../atoms/Badge';
import { InformationBanner } from '../../InformationBanner';
import {
  FieldWithTextRange,
  TextDeletionInput,
} from '../../TextDeletionInput/TextDeletionInput';
import { StepperModalHeader } from '../RemovePIIDataModal.styles';
import {
  CheckboxContainer,
  CheckboxDescription,
  CheckboxDescriptionContainer,
  ItemTitle,
  SectionContainer,
} from './ReporterInfoStep.styles';

export const InfoBannerText = (
  <>
    <strong>Highlight the text</strong> you want to delete and{' '}
    <strong>click the &quot;Delete&quot; button</strong>. Strikethrough will
    indicate marked for deletion texts.
  </>
);

interface ReporterInfoStepProps {
  payload: DataDeletionType;
  setPayload: Dispatch<SetStateAction<DataDeletionType>>;
  isReporterAnonymous: boolean | undefined;
  isReporterKnown: boolean | undefined;
  warningMessage: {
    title?: string;
    description: string;
  };
  reporterNotes?: string | null;
  onTextRangeDeleteRequested: (
    range: TextRange,
    field: FieldWithTextRange,
  ) => void;
  handleTextFieldClearAll: (field: FieldWithTextRange) => void;
}

export const ReporterInfoStep = ({
  payload,
  setPayload,
  isReporterAnonymous,
  isReporterKnown,
  warningMessage,
  reporterNotes,
  onTextRangeDeleteRequested,
  handleTextFieldClearAll,
}: ReporterInfoStepProps) => {
  const handleChange = useCallback(
    (value: boolean) => {
      setPayload((prevPayload: DataDeletionType) => ({
        ...prevPayload,
        anonymiseReporter: value,
      }));
    },
    [setPayload],
  );
  const isDisabled = isReporterAnonymous || !isReporterKnown;

  const hasReporterNotes = reporterNotes && Boolean(reporterNotes.length);

  const reporterDataItems = [
    'Reporter name',
    'Email',
    'Role',
    'Employee ID',
    'Department',
    'Site',
    'Country',
    'Notification email',
  ];

  return (
    <>
      <StepperModalHeader data-testid="remove-pii-data-modal-header">
        Select reporter information to permanently remove
      </StepperModalHeader>
      <InformationBanner
        state={FormState.WARNING}
        title={warningMessage.title ?? ''}
        description={warningMessage.description ?? ''}
        $centerIconVertically={!warningMessage.title}
        $fullWidth
      />
      <SectionContainer>
        <ItemTitle>Reporter Information</ItemTitle>
        <CheckboxContainer>
          <Checkbox
            checked={Boolean(isReporterAnonymous) || payload.anonymiseReporter}
            onChange={() => handleChange(!payload.anonymiseReporter)}
            data-testid="anonymise-reporter-checkbox"
            disabled={isDisabled}
            color={'primary'}
          />
          <CheckboxDescription $disabled={isDisabled}>
            Permanently anonymise reporter data. If applied on a VaultTalk
            report, this will remove access to all audio files
          </CheckboxDescription>
        </CheckboxContainer>
        <CheckboxDescriptionContainer>
          {reporterDataItems.map((item) => (
            <Badge key={item} variant="new" text={item} />
          ))}
        </CheckboxDescriptionContainer>
      </SectionContainer>

      {hasReporterNotes && (
        <SectionContainer>
          <InformationBanner
            state={FormState.DEFAULT}
            description={InfoBannerText}
            $fullWidth
          />
          <ItemTitle>Reporter Notes</ItemTitle>
          <TextDeletionInput
            field="reporterNotes"
            value={reporterNotes}
            onTextRangeDeleteRequested={onTextRangeDeleteRequested}
            handleTextFieldClearAll={handleTextFieldClearAll}
            appliedRanges={payload.reporterNotes || []}
          />
        </SectionContainer>
      )}
    </>
  );
};
